/* === AgMenu -- Anagogia Design System === */

import { useEffect, useId, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { useCalqueEchap } from '../utils/useCalqueEchap';
import { usePortalContainer } from '../utils/usePortalContainer';

import styles from './AgMenu.module.css';

/* ------------------------------------------------------------------ */
/*  AgMenuItem                                                        */
/* ------------------------------------------------------------------ */

interface AgMenuItemProps {
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /**
   * Une ligne sous le libellé (la raison d'une ligne inerte, une précision) : visible sans survol, lue par
   * le lecteur d'écran par `aria-describedby`, hors du nom accessible de l'élément. Jamais un `title` seul,
   * invisible au clavier.
   */
  description?: React.ReactNode;
}

/**
 * Élément cliquable d'un AgMenu, avec icône optionnelle et description optionnelle sous le libellé.
 * @category Overlays
 */
function AgMenuItem({
  onClick,
  disabled = false,
  icon,
  children,
  className,
  description,
}: AgMenuItemProps) {
  const descriptionId = useId();
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={[
        styles.menuItem,
        disabled ? styles.menuItemDisabled : undefined,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-describedby={description ? descriptionId : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {icon && <span className={styles.menuItemIcon}>{icon}</span>}
      <span className={[styles.menuItemLabel, description ? styles.menuItemLabelDecrit : undefined].filter(Boolean).join(' ')}>
        {children}
        {description && (
          // aria-hidden : la description est lue par aria-describedby, pas dans le nom de l'élément.
          <span id={descriptionId} className={styles.menuItemDescription} aria-hidden="true">{description}</span>
        )}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AgMenu                                                            */
/* ------------------------------------------------------------------ */

interface AgMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  placement?: 'bottom-start' | 'bottom-end';
  className?: string;
}

interface MenuPosition {
  top: number;
  left: number;
}

function AgMenuPortal({
  anchorEl,
  onClose,
  children,
  placement = 'bottom-start',
  className,
}: Omit<AgMenuProps, 'open'>) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });

  // Compute position relative to anchor
  const updatePosition = useCallback(() => {
    if (!anchorEl || !menuRef.current) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const gap = 4;

    let top = anchorRect.bottom + gap;
    let left =
      placement === 'bottom-end'
        ? anchorRect.right - menuRect.width
        : anchorRect.left;

    // Clamp to viewport
    const padding = 8;
    if (left + menuRect.width > window.innerWidth - padding) {
      left = window.innerWidth - menuRect.width - padding;
    }
    if (left < padding) {
      left = padding;
    }
    if (top + menuRect.height > window.innerHeight - padding) {
      // Flip above anchor
      top = anchorRect.top - menuRect.height - gap;
    }
    if (top < padding) {
      top = padding;
    }

    setPosition({ top, left });
  }, [anchorEl, placement]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Échap : pile commune des calques (le menu ne ferme pas la fenêtre dessous)
  useCalqueEchap(true, onClose);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchorEl &&
        !anchorEl.contains(target)
      ) {
        onClose();
      }
    };

    // Use mousedown to close before the click propagates
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorEl]);

  return (
    <div
      ref={menuRef}
      className={[styles.menu, className].filter(Boolean).join(' ')}
      role="menu"
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>
  );
}

/**
 * Menu contextuel ancré rendu en portal (contient des AgMenuItem).
 * @category Overlays
 */
function AgMenu(props: AgMenuProps) {
  // En plein écran, un portail dans `document.body` n'est jamais peint :
  // le menu suit l'élément plein écran qui contient son ancre.
  const portalContainer = usePortalContainer(props.anchorEl, props.open);

  if (!props.open || !props.anchorEl) return null;

  return createPortal(
    <AgMenuPortal
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      placement={props.placement}
      className={props.className}
    >
      {props.children}
    </AgMenuPortal>,
    portalContainer,
  );
}

export { AgMenu, AgMenuItem };
export type { AgMenuProps, AgMenuItemProps };
export default AgMenu;
